import React, { useState, useEffect } from 'react';
import { X, Download, Loader2, FileText, Image, File, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { adjustUrlForDocumentAccess } from '../../services/cloudinaryService';

// Exportar para que sea considerado un módulo
export {};

interface DocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documentUrl: string;
  documentName: string;
}

const DocumentViewer = ({ isOpen, onClose, documentUrl, documentName }: DocumentViewerProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [documentType, setDocumentType] = useState<'image' | 'pdf' | 'other'>('other');
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (documentUrl && isOpen) {
      setIsLoading(true);
      setHasError(false);
      
      // Determinar el tipo de documento basado en la URL o extensión
      const extension = documentName.split('.').pop()?.toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')) {
        setDocumentType('image');
      } else if (extension === 'pdf') {
        setDocumentType('pdf');
      } else {
        setDocumentType('other');
      }
    }
  }, [documentUrl, documentName, isOpen]);

  // Ajustar la URL para mejor acceso a documentos
  const adjustedDocumentUrl = adjustUrlForDocumentAccess(documentUrl);

  const handleDownload = async () => {
    try {
      // Para forzar la descarga, usamos fetch para obtener el archivo como blob
      const response = await fetch(adjustedDocumentUrl);
      if (!response.ok) {
        throw new Error('Error al descargar el archivo');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Crear un enlace temporal para descargar el archivo
      const link = document.createElement('a');
      link.href = url;
      link.download = documentName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar el objeto URL creado
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      // Si falla, intentamos con el método tradicional
      const link = document.createElement('a');
      link.href = adjustedDocumentUrl;
      link.download = documentName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handlePdfError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const getDocumentIcon = () => {
    switch (documentType) {
      case 'image':
        return <Image className="w-8 h-8 text-blue-500" />;
      case 'pdf':
        return <FileText className="w-8 h-8 text-red-500" />;
      default:
        return <File className="w-8 h-8 text-gray-500" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getDocumentIcon()}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 truncate max-w-md">
                {documentName}
              </h3>
              <p className="text-sm text-gray-500 capitalize">
                {documentType === 'image' ? 'Imagen' : documentType === 'pdf' ? 'PDF' : 'Documento'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleDownload}
              variant="outline"
              size="sm"
              className="flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Descargar</span>
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="p-2"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50">
          {isLoading && (
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mb-2" />
              <p className="text-gray-600">Cargando documento...</p>
            </div>
          )}

          {hasError ? (
            <div className="text-center p-8 max-w-md">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
                Error al cargar el documento
              </h3>
              <p className="text-gray-600 mb-6">
                No se pudo cargar el documento para su visualización. Esto puede deberse a restricciones de acceso o formato no compatible.
              </p>
              <Button
                onClick={handleDownload}
                className="flex items-center space-x-2 mx-auto"
              >
                <Download className="w-4 h-4" />
                <span>Descargar {documentName}</span>
              </Button>
            </div>
          ) : (
            <>
              {documentType === 'image' && (
                <div className="relative max-w-full max-h-full">
                  <img
                    src={adjustedDocumentUrl}
                    alt={documentName}
                    className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ display: isLoading ? 'none' : 'block' }}
                  />
                </div>
              )}

              {documentType === 'pdf' && (
                <div className="w-full h-full flex flex-col">
                  {isLoading && (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-2" />
                        <p className="text-gray-600">Cargando PDF...</p>
                      </div>
                    </div>
                  )}
                  <iframe
                    src={adjustedDocumentUrl}
                    title={documentName}
                    className="w-full h-full min-h-[600px] rounded-lg shadow-lg"
                    onLoad={() => setIsLoading(false)}
                    onError={handlePdfError}
                    style={{ display: isLoading ? 'none' : 'block' }}
                  />
                </div>
              )}

              {documentType === 'other' && (
                <div className="text-center p-8">
                  {getDocumentIcon()}
                  <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
                    Vista previa no disponible
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Este tipo de archivo no se puede previsualizar. Puedes descargarlo para ver su contenido.
                  </p>
                  <Button
                    onClick={handleDownload}
                    className="flex items-center space-x-2 mx-auto"
                  >
                    <Download className="w-4 h-4" />
                    <span>Descargar {documentName}</span>
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;